import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Image, ImageSourcePropType, StyleSheet, Text, View } from 'react-native';
import Toast from 'react-native-toast-message';

import { IMiniGameProps } from '../../../../../../typings/common';
import { BGHorDivider, BGTimerWrapper, ChipPng } from '../../../../../assets';
import BGPlayPaidBtn from '../../../../../assets/bonusGames/play.png';
import Centered from '../../../../../shared/Centered';
import { inset } from '../../../../../utils/helpers/css';
import { useBonusCountdown } from '../../../../../utils/hooks/useBonusCountdown';
import { Pressable, TouchableOpacity } from '../../../common/Touchable';

type BonusGameCardProps = Pick<
	IMiniGameProps,
	| 'isSmall'
	| 'onStartGame'
	| 'onRequireLogin'
	| 'canPlay'
	| 'config'
	| 'miniGameData'
	| 'playerLevel'
> & {
	titleSource: ImageSourcePropType;
	thumbSource: ImageSourcePropType;
	launchPath: string;
	cooldownHours: number;
	freePlayTitleFontSize: number;
	infoPaddingVertical: number;
};

function BonusGameCard({
	isSmall,
	onStartGame,
	onRequireLogin,
	canPlay = true,
	config: { url, token, language },
	miniGameData,
	playerLevel,
	titleSource,
	thumbSource,
	launchPath,
	cooldownHours,
	freePlayTitleFontSize,
	infoPaddingVertical,
}: BonusGameCardProps) {
	const { t } = useTranslation();
	const collectedAt = miniGameData?.collected_at;
	const { timeText, ready: isFreePlayReady } = useBonusCountdown(collectedAt, cooldownHours);
	const isPlayMiniGameDisabled =
		!canPlay || !miniGameData || (!isFreePlayReady && (playerLevel ?? 0) < 10);
	const showPaidPlayState = !!miniGameData && (playerLevel ?? 0) >= 10 && !isFreePlayReady;
	const paidPlayStake = miniGameData?.stake ?? 500;

	const freePlayIn = !canPlay ? '' : collectedAt ? timeText : '00:00:00';

	const canShowDivider = !isSmall;

	const thumbAnim = useRef(new Animated.Value(0)).current;
	const loopRef = useRef<Animated.CompositeAnimation | null>(null);

	const thumbTranslateY = thumbAnim.interpolate({
		inputRange: [0, 1],
		outputRange: [0, -10],
	});

	const handleHoverIn = useCallback(() => {
		if (loopRef.current) {
			loopRef.current.stop();
		}
		const loop = Animated.loop(
			Animated.sequence([
				Animated.timing(thumbAnim, {
					toValue: 1,
					duration: 350,
					useNativeDriver: true,
				}),
				Animated.timing(thumbAnim, {
					toValue: 0,
					duration: 350,
					useNativeDriver: true,
				}),
			]),
		);
		loopRef.current = loop;
		loop.start();
	}, [thumbAnim]);

	const handleHoverOut = useCallback(() => {
		if (loopRef.current) {
			loopRef.current.stop();
			loopRef.current = null;
		}
		Animated.timing(thumbAnim, {
			toValue: 0,
			duration: 200,
			useNativeDriver: true,
		}).start();
	}, [thumbAnim]);

	const launchGame = useCallback(() => {
		if (!canPlay) {
			onRequireLogin?.();
			return;
		}

		if (isPlayMiniGameDisabled) {
			if ((playerLevel ?? 0) < 10) {
				Toast.show({
					type: 'info',
					text1: t('T_SITE_MODAL_GAME_PLAY_LEVEL_ERROR').replace('%%', '10'),
					position: 'bottom',
					visibilityTime: 3000,
				});
			}
			return;
		}
		onStartGame(`${launchPath}?gameApiUrl=${url}&token=${token}&language=${language}`);
	}, [
		canPlay,
		isPlayMiniGameDisabled,
		launchPath,
		onRequireLogin,
		onStartGame,
		playerLevel,
		t,
		url,
		token,
		language,
	]);

	return (
		<View
			style={{
				width: isSmall ? '100%' : undefined,
				gap: isSmall ? 2 : 12,
				alignItems: 'center',
				flex: isSmall ? undefined : 1,
			}}
		>
			<Image source={titleSource} resizeMode='contain' style={styles.titleImage} />

			<View
				style={{
					flexDirection: 'row',
					alignItems: 'center',
					width: isSmall ? '100%' : '86%',
					minHeight: 110,
					gap: 10,
				}}
			>
				<Animated.View
					style={{
						transform: [{ translateY: thumbTranslateY }],
					}}
				>
					<Pressable onPress={launchGame} onHoverIn={handleHoverIn} onHoverOut={handleHoverOut}>
						<Image
							source={thumbSource}
							resizeMode='contain'
							style={{
								width: isSmall ? 70 : 80,
								height: isSmall ? 70 : 80,
								marginTop: isSmall ? 10 : 0,
							}}
						/>
					</Pressable>
				</Animated.View>

				<View
					style={{
						flex: 1,
						alignItems: 'center',
						gap: 2,
						paddingVertical: infoPaddingVertical,
						paddingRight: isSmall ? 4 : 12,
					}}
				>
					<Text
						style={{
							fontWeight: 'bold',
							fontSize: freePlayTitleFontSize,
							color: 'white',
							textAlign: 'center',
						}}
					>
						{t('T_SITE_MODAL_BONUS_FREE_PLAY')}
					</Text>

					{canPlay && (
						<View
							style={{
								width: isSmall ? 165 : 140,
								height: isSmall ? 54 : 38,
								position: 'relative',
							}}
						>
							<Image source={BGTimerWrapper} resizeMode='stretch' style={styles.timerImageFill} />
							<Centered style={{ ...inset(0), position: 'absolute' }}>
								<Text
									style={{
										color: 'white',
										fontWeight: '900',
										fontSize: isSmall ? 12 : 11,
										textAlign: 'center',
									}}
									numberOfLines={1}
								>
									{freePlayIn}
								</Text>
							</Centered>
						</View>
					)}

					<TouchableOpacity activeOpacity={0.75} onPress={launchGame}>
						<View style={styles.playButtonWrapper}>
							<Image
								source={BGPlayPaidBtn}
								resizeMode='stretch'
								style={{
									width: isSmall ? 108 : 92,
									height: isSmall ? 40 : 30,
									opacity: isPlayMiniGameDisabled ? 0.6 : 1,
								}}
							/>
							<View style={styles.playButtonOverlay}>
								{showPaidPlayState ? (
									<View style={styles.paidRow}>
										<Text style={styles.playText}>{t('T_SITE_MODAL_BONUS_PLAY')}</Text>
										<View style={styles.amountRow}>
											<Image source={ChipPng} style={styles.stakeChip} />
											<Text style={styles.stakeText}>{paidPlayStake}</Text>
											<Text style={styles.plusText}>+</Text>
										</View>
									</View>
								) : (
									<Text style={styles.playText}>{t('T_SITE_MODAL_BONUS_PLAY')}</Text>
								)}
							</View>
						</View>
					</TouchableOpacity>
				</View>
			</View>

			{canShowDivider && (
				<Image
					source={BGHorDivider}
					resizeMode='contain'
					style={{
						width: '100%',
						aspectRatio: 27.5,
						maxWidth: isSmall ? 220 : undefined,
						alignSelf: 'center',
					}}
				/>
			)}
		</View>
	);
}

const styles = StyleSheet.create({
	titleImage: {
		width: 180,
		height: 40,
	},
	timerImageFill: {
		width: '100%',
		height: '100%',
	},
	playButtonWrapper: {
		position: 'relative',
	},
	playButtonOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		alignItems: 'center',
		justifyContent: 'center',
		gap: 0,
	},
	playText: {
		color: '#fff',
		fontWeight: 'bold',
		fontSize: 11,
		textTransform: 'uppercase',
	},
	stakeChip: {
		width: 11,
		height: 11,
		transform: [{ translateY: 1 }],
	},
	stakeText: {
		color: '#fff',
		fontWeight: 'bold',
		fontSize: 11,
		marginLeft: -1,
	},
	plusText: {
		color: '#fff',
		fontWeight: 'bold',
		fontSize: 11,
		marginLeft: -2,
		transform: [{ translateY: -0.5 }],
	},
	paidRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
	},
	amountRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 2,
	},
});

export default BonusGameCard;
